import { useCallback } from "react";
import Label from "../label/Label";
import { cn } from "@/lib/styles.utils";
import { Icon } from "../icons/Icon";

type Size = "large" | "medium";

interface TextareaProps {
  label: string;
  placeholder?: string;
  value: string;
  onChange: (snData: string) => void;
  size: Size;
  disabled?: boolean;
  readOnly?: boolean;
  required?: boolean;
  warning?: boolean;
  error?: boolean;
  success?: boolean;
  helpText?: string;
  name: string;
  width?: string;
  deleteMessage?: string;
  maxlength?: number;
  rows?: number;
}

const normalize = (text: string) =>
  text
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replaceAll(" ", "-")
    .toLowerCase();

const Textarea = ({
  label,
  placeholder,
  value,
  size,
  onChange,
  disabled,
  readOnly,
  required,
  warning,
  error,
  success,
  helpText,
  name,
  width,
  deleteMessage,
  maxlength,
  rows,
}: TextareaProps) => {
  const id = normalize(name);
  const requiredAlert = Boolean(required && value.trim().length === 0);

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      onChange(e.target.value);
    },
    [onChange],
  );

  const cleanMessage = useCallback(() => onChange(""), [onChange]);

  const borderState =
    error || requiredAlert
      ? "border-ib-pt-color-red-500"
      : warning
        ? "border-ib-pt-color-orange-500"
        : success
          ? "border-ib-pt-color-green-1000"
          : "border-ib-pt-color-gray-300";

  return (
    <div style={{ width: width ?? "100%" }}>
      <div className="mb-2 flex items-center gap-x-2 justify-between">
        <Label label={label} id={id} required={required} />
        {deleteMessage && value?.length > 0 ? (
          <button
            type="button"
            className="text-[14px] leading-4 text-ib-pt-color-red-500 underline"
            onClick={cleanMessage}
          >
            {deleteMessage}
          </button>
        ) : null}
      </div>
      <textarea
        id={id}
        name={id}
        rows={rows || 5}
        disabled={disabled}
        readOnly={readOnly}
        required={required}
        placeholder={placeholder || ""}
        value={value}
        onChange={handleChange}
        className={cn(
          "text-[14px] leading-5 w-full rounded-[2px] border bg-ib-pt-color-white text-ib-pt-color-neutral-800",
          "read-only:bg-ib-pt-color-gray-100 read-only:text-ib-pt-color-neutral-800",
          "disabled:border-ib-pt-color-gray-300 read-only:border-ib-pt-color-gray-300",
          "resize-none",
          size === "medium" ? "px-3 py-[9px]" : "px-3 py-[13px]",
          borderState,
          !requiredAlert && !readOnly
            ? "hover:border-ib-pt-color-red-300 focus:border-ib-pt-color-red-500"
            : "",
          "focus:outline-none",
          disabled
            ? "placeholder:text-ib-pt-color-gray-800"
            : "placeholder:text-ib-pt-color-neutral-500",
        )}
        maxLength={maxlength || 500}
      />

      {helpText && (
        <div className="flex gap-2 items-center">
          <Icon name="info" className="size-4 text-ib-pt-color-gray-800" />
          <p className="text-sm text-ib-pt-color-gray-800">{helpText}</p>
        </div>
      )}
    </div>
  );
};

export default Textarea;
